import { useCallback, useEffect, useRef, type RefObject } from 'react';

/** A second tap on the same action within this long of the first is the same tap (a fat-finger double tap). */
export const TAP_GUARD_MS = 600;

type Entry = { at: number; busy: boolean };

/**
 * One guard for a screen's action buttons (rule 19: a double tap is one tap). `guard(key, action)` returns a handler
 * that ignores a call while the last call with that key is still running (its promise is pending) or within
 * TAP_GUARD_MS of it. Buttons that sit in the same spot one after the other (an Undo on each row, a bottom button
 * that turns into the next screen's button, a sheet that opens over the button that opened it) share one key, so the
 * second tap of a double tap lands on nothing.
 *
 * The check is synchronous: React state (`loading`) updates only after a re-render, too late for a tap 60 ms later.
 */
export const createTapGuard = (now: () => number = () => Date.now()) => {
  const last = new Map<string, Entry>();
  return <A extends unknown[], R>(key: string, action: (...args: A) => R) =>
    (...args: A): R | undefined => {
      const t = now();
      const prev = last.get(key);
      if (prev && (prev.busy || t - prev.at < TAP_GUARD_MS)) return undefined;
      const entry: Entry = { at: t, busy: true };
      last.set(key, entry);
      let result: R;
      try {
        result = action(...args);
      } catch (e) {
        entry.busy = false;
        throw e;
      }
      if (result && typeof (result as { then?: unknown }).then === 'function') {
        (result as unknown as Promise<unknown>).then(
          () => (entry.busy = false),
          () => (entry.busy = false),
        );
      } else {
        entry.busy = false;
      }
      return result;
    };
};

export type TapGuard = ReturnType<typeof createTapGuard>;

/** The screen's tap guard (see createTapGuard), the same one for the life of the screen. */
export const useTapGuard = (): TapGuard => {
  const guard = useRef<TapGuard>();
  if (!guard.current) guard.current = createTapGuard();
  return useCallback<TapGuard>((key, action) => guard.current!(key, action), []);
};

const systemNow = () => Date.now();

type ClickLike = {
  detail?: number;
  target?: EventTarget | null;
  stopPropagation: () => void;
  preventDefault: () => void;
};

/**
 * When the controls under the finger last appeared or changed, and when this shield last swallowed a tap. A tap is
 * swallowed within TAP_GUARD_MS of the change, and so is every tap that follows a swallowed one within TAP_GUARD_MS:
 * a thumb still tapping away (at a button that showed "loading", say) never reaches what appeared under it. A pause
 * of TAP_GUARD_MS ends the streak; the next tap is deliberate.
 */
type Shield = { changedAt?: number; swallowedAt?: number };

const swallows = (shield: Shield, t: number) => {
  const fresh = shield.changedAt !== undefined && t - shield.changedAt < TAP_GUARD_MS;
  const streak = shield.swallowedAt !== undefined && t - shield.swallowedAt < TAP_GUARD_MS;
  if (fresh || streak) shield.swallowedAt = t;
  return fresh || streak;
};

/** Only a pointer's click (a tap or a mouse click: `detail` >= 1); a keyboard's Enter or Space (`detail` 0) is deliberate. */
const isPointerClick = (e: ClickLike) => (e.detail ?? 0) > 0;

const swallow = (e: ClickLike) => {
  e.stopPropagation();
  e.preventDefault();
};

/**
 * A sheet or dialog that just appeared under the finger takes no tap for TAP_GUARD_MS (rule 19). Round 29, Sunita: a
 * 60 ms double tap on the package page's bottom Pack opened the pack drawer and the second tap landed on Confirm
 * packing, in the same spot, and packed the parcel. Returns a click-capture handler for the sheet's content (antd
 * `modalRender` / `drawerRender`): within TAP_GUARD_MS of `open` turning true, a tap inside is swallowed, and so is a
 * tap that keeps a streak of swallowed taps going (round 30: a thumb tapping on while Pack loads). Only a pointer's
 * click is; a keyboard's Enter or Space on a focused button (`detail` 0) is a deliberate act and goes through.
 */
export const useJustShownShield = (open: boolean, now: () => number = systemNow) => {
  const shield = useRef<Shield>({});
  const wasOpen = useRef(false);
  if (open && !wasOpen.current) shield.current = { changedAt: now() };
  wasOpen.current = open;
  return useCallback(
    (e: ClickLike) => {
      if (isPointerClick(e) && swallows(shield.current, now())) swallow(e);
    },
    [now],
  );
};

/**
 * A floor screen's step changed under the finger (rule 19, round 31, Deepa): Put back's "Found a missing piece" opened
 * the shelf step, and the second tap of her double tap fired its "Scan shelf label", in the same spot, and opened the
 * camera. Returns a click-capture handler for the screen: within TAP_GUARD_MS of `step` changing (compared with
 * Object.is; the first render is not a change), a tap is swallowed, and so is a tap that keeps a streak of swallowed
 * taps going. Only a pointer's click is (see useJustShownShield).
 */
export const useStepShield = (step: unknown, now: () => number = systemNow) => {
  const shield = useRef<Shield>({});
  const seen = useRef<{ step: unknown }>();
  if (!seen.current) seen.current = { step };
  else if (!Object.is(seen.current.step, step)) {
    seen.current = { step };
    shield.current = { changedAt: now() };
  }
  return useCallback(
    (e: ClickLike) => {
      if (isPointerClick(e) && swallows(shield.current, now())) swallow(e);
    },
    [now],
  );
};

/**
 * What the bottom bar offers, as the worker reads it: each control's words, counts left out ("Done: close with N pcs"),
 * and where it sits on the screen. A line that appears in the bar and moves its buttons under the thumb is a change too
 * (round 43, Imran: "Couldn't reach the server" appeared in the bar, the bar grew down by its height, and the second tap
 * of a double tap on "Done receiving" landed on "Scan pieces in" and opened the camera). Scrolling is not a change: the
 * place is read again on every scroll, quietly (see useSwapShield).
 */
const offered = (bar: HTMLElement) =>
  Array.from(bar.querySelectorAll('button, a[href], [role="button"]'))
    .map(control => {
      const words = (control.textContent ?? '').replace(/\d+/g, '#').replace(/\s+/g, ' ').trim();
      const r = control.getBoundingClientRect();
      return `${words}@${Math.round(r.left / 8)},${Math.round(r.top / 8)}`;
    })
    .join('|');

/**
 * The bottom bar's buttons take no tap for TAP_GUARD_MS after they appear or change to another action (rule 19,
 * round 30, Sunita): Pack, then Confirm packing, then Ready To Ship sit in the same thumb spot one after another, so a
 * habit tap meant for the old button must not fire the new one. A count in a label changing ("38 pcs" → "39 pcs") is
 * not a new action. Returns a click-capture handler for the bar; only taps on the bar's own elements are judged (a
 * sheet the bar's button opened has its own shield).
 */
export const useSwapShield = (
  bar: RefObject<HTMLElement | null>,
  now: () => number = systemNow,
) => {
  const shield = useRef<Shield>({});
  const seen = useRef<string>();
  const watched = useRef<{ el: HTMLElement; watch: MutationObserver; unlisten: () => void }>();
  // After every render: the bar may mount later than the screen (or be replaced), and is watched from then on.
  useEffect(() => {
    const el = bar.current ?? undefined;
    if (watched.current?.el === el) return;
    watched.current?.watch.disconnect();
    watched.current?.unlisten();
    watched.current = undefined;
    if (!el) return;
    // Appearing is a change: the bar of the next screen lands under the thumb that finished the last one.
    seen.current = offered(el);
    shield.current = { changedAt: now() };
    const watch = new MutationObserver(() => {
      const next = offered(el);
      if (next === seen.current) return;
      seen.current = next;
      shield.current = { changedAt: now() };
    });
    watch.observe(el, { childList: true, subtree: true, characterData: true });
    // The worker scrolling or turning the phone moves the bar, never under a habit tap: its place is read again.
    const reread = () => {
      seen.current = offered(el);
    };
    document.addEventListener('scroll', reread, { capture: true, passive: true });
    window.addEventListener('resize', reread);
    const unlisten = () => {
      document.removeEventListener('scroll', reread, { capture: true });
      window.removeEventListener('resize', reread);
    };
    watched.current = { el, watch, unlisten };
  });
  useEffect(
    () => () => {
      watched.current?.watch.disconnect();
      watched.current?.unlisten();
      watched.current = undefined;
    },
    [],
  );
  return useCallback(
    (e: ClickLike) => {
      const el = bar.current;
      const target = e.target as Node | null | undefined;
      if (!el || !target || !el.contains(target)) return;
      if (isPointerClick(e) && swallows(shield.current, now())) swallow(e);
    },
    [bar, now],
  );
};
